import path from "node:path";
import { api, listMyOrgs, type Org } from "../lib/api.js";
import { saveProject } from "../lib/config.js";

interface AppDetail {
  id: string;
  name: string;
  slug: string;
  app_token: string;
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

async function findByAppId(orgs: Org[], appId: string) {
  for (const org of orgs) {
    const res = await api.get(`/v1/apps/${appId}`, { orgId: org.id });
    if (res.ok) {
      return { app: (await res.json()) as AppDetail, org };
    }
    if (res.status !== 404) {
      const err = (await res.json().catch(() => ({}))) as { error?: string };
      console.error(`Link failed (${org.name}): ${err.error || res.statusText}`);
      process.exit(1);
    }
  }
  return null;
}

async function findBySlug(orgs: Org[], slug: string) {
  for (const org of orgs) {
    const res = await api.get(`/v1/apps`, { orgId: org.id });
    if (!res.ok) {
      if (res.status === 404) continue;
      const err = (await res.json().catch(() => ({}))) as { error?: string };
      console.error(`Link failed (${org.name}): ${err.error || res.statusText}`);
      process.exit(1);
    }
    const payload = (await res.json()) as { apps: AppDetail[] } | AppDetail[];
    const apps = Array.isArray(payload) ? payload : payload.apps;
    const match = apps.find((a) => a.slug === slug);
    if (match) {
      // Re-fetch by id to get app_token (list endpoint may omit it).
      const detailRes = await api.get(`/v1/apps/${match.id}`, { orgId: org.id });
      if (detailRes.ok) {
        return { app: (await detailRes.json()) as AppDetail, org };
      }
      return { app: match, org };
    }
  }
  return null;
}

export async function linkCommand(
  appIdOrSlug: string,
  options?: { dir?: string; org?: string },
): Promise<void> {
  const dir = options?.dir ? path.resolve(options.dir) : process.cwd();

  const orgs = await listMyOrgs();
  if (orgs.length === 0) {
    console.error("No organizations found. Run `clawnify login` first.");
    process.exit(1);
  }

  const candidates = options?.org
    ? orgs.filter((o) => o.id === options.org || o.slug === options.org)
    : orgs;

  if (candidates.length === 0) {
    console.error(
      `No org matched "${options?.org}". Available: ${orgs.map((o) => o.slug).join(", ")}`,
    );
    process.exit(1);
  }

  const isUuid = UUID_RE.test(appIdOrSlug);
  const matched = isUuid
    ? await findByAppId(candidates, appIdOrSlug)
    : await findBySlug(candidates, appIdOrSlug);

  if (!matched) {
    const kind = isUuid ? "id" : "slug";
    console.error(
      `App with ${kind} '${appIdOrSlug}' not found in ${candidates.map((o) => o.slug).join(", ")}.`,
    );
    process.exit(1);
  }

  saveProject(dir, {
    org_id: matched.org.id,
    app_id: matched.app.id,
    slug: matched.app.slug,
    app_token: matched.app.app_token,
  });

  const rel = path.relative(process.cwd(), dir) || ".";
  console.log(`  Linked ${matched.app.name} (${matched.app.slug}) in ${matched.org.name}`);
  console.log(`  Wrote ${rel}/.clawnify/project.json`);
  console.log(`  Run \`clawnify deploy\` to push the current source to this app.`);
}
